<template>
  <!-- ====================== SEÇÃO: DEPOIMENTOS ====================== -->
  <section class="testimonials-section px-4 lg:px-0 mt-10 mb-10" id="depoimentos">
    <div class="max-w-screen-xl mx-auto">

      <!-- Cabeçalho com título e botões -->
      <div class="flex justify-between items-center mb-12">
        <div class="flex-1">
          <div class="flex items-center justify-between gap-2 relative pb-3">
            <div>
              <h2
                class="text-lg md:text-xl font-medium tracking-wide"
                :style="{ color: customization?.section_title_color || '#1c243b' }"
              >
                {{ customization?.testimonials_title || 'Depoimentos' }}
              </h2>
              <h2
                class="text-xs md:text-sm font-normal tracking-wide"
                :style="{ color: customization?.section_subtitle_color || '#666666' }"
              >
                {{ customization?.testimonials_subtitle || 'Veja o que quem já participou tem a dizer' }}
              </h2>
            </div>

            <!-- Botões de navegação - Desktop (28px × 28px) -->
            <div class="hidden md:flex items-center gap-2" v-if="maxTestimonialSlide > 0">
              <button
                @click="prevTestimonial"
                @mouseenter="isPrevHovered = true"
                @mouseleave="isPrevHovered = false"
                :disabled="testimonialSlide === 0"
                :class="{ 'opacity-50 cursor-not-allowed': testimonialSlide === 0 }"
                class="flex items-center justify-center w-4 h-4 rounded-md transition-all shadow-md"
                :style="arrowButtonStyle(isPrevHovered && testimonialSlide !== 0)"
              >
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                @click="nextTestimonial"
                @mouseenter="isNextHovered = true"
                @mouseleave="isNextHovered = false"
                :disabled="testimonialSlide === maxTestimonialSlide"
                :class="{ 'opacity-50 cursor-not-allowed': testimonialSlide === maxTestimonialSlide }"
                class="flex items-center justify-center w-4 h-4 rounded-md transition-all shadow-md"
                :style="arrowButtonStyle(isNextHovered && testimonialSlide !== maxTestimonialSlide)"
              >
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <!-- Linhas decorativas -->
            <div class="absolute bottom-0 left-0 w-full h-px bg-gray-200"></div>
            <div
              class="absolute bottom-0 left-0 h-px w-full max-w-[480px]"
              :style="{ backgroundColor: customization?.section_divider_color || '#e5e7eb' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Carrossel de Depoimentos -->
      <div v-if="testimonials && testimonials.length > 0" class="relative -mx-4 lg:-mx-8">
        <div class="overflow-hidden px-4 lg:px-8">
          <div
            class="flex transition-transform duration-500 ease-in-out -mx-3"
            :style="{ transform: `translateX(-${testimonialSlide * (100 / testimonialSlidesToShow)}%)` }"
          >
            <div
              v-for="(testimonial, index) in testimonials"
              :key="index"
              class="flex-shrink-0 px-3"
              :class="testimonialSlidesToShow === 1 ? 'w-full' : 'w-full md:w-1/3'"
            >
              <div class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300 h-full flex flex-col">
                <div class="mb-4">
                  <svg class="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10... (seu SVG de aspas)"/>
                  </svg>
                </div>

                <p class="text-gray-700 text-sm leading-relaxed mb-6 flex-grow">
                  {{ testimonial.testimonial }}
                </p>

                <div class="flex items-center mt-auto">
                  <img :src="testimonial.photo" :alt="testimonial.name" class="w-12 h-12 rounded-md object-cover mr-3">
                  <div>
                    <h4 class="font-semibold text-gray-900 text-sm">{{ testimonial.name }}</h4>
                    <p class="text-xs text-gray-600">{{ testimonial.position }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botões Mobile (40px × 40px) -->
        <div class="flex justify-center mt-8 space-x-3 md:hidden" v-if="maxTestimonialSlide > 0">
          <button
            @click="prevTestimonial"
            :disabled="testimonialSlide === 0"
            :class="{ 'opacity-50 cursor-not-allowed': testimonialSlide === 0 }"
            class="flex items-center justify-center w-5 h-5 rounded-lg transition-all shadow-md"
            :style="arrowButtonStyle(testimonialSlide !== 0)"
            @touchstart.prevent="testimonialSlide !== 0 && (isPrevHovered = true)"
            @touchend.prevent="isPrevHovered = false"
          >
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            @click="nextTestimonial"
            :disabled="testimonialSlide === maxTestimonialSlide"
            :class="{ 'opacity-50 cursor-not-allowed': testimonialSlide === maxTestimonialSlide }"
            class="flex items-center justify-center w-5 h-5 rounded-lg transition-all shadow-md"
            :style="arrowButtonStyle(testimonialSlide !== maxTestimonialSlide)"
            @touchstart.prevent="testimonialSlide !== maxTestimonialSlide && (isNextHovered = true)"
            @touchend.prevent="isNextHovered = false"
          >
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div v-else class="text-center py-12">
        <p class="text-gray-500">Nenhum depoimento cadastrado ainda.</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useLandingPage } from '@/composables/useLandingPage'

const { testimonials, customization } = useLandingPage()

const testimonialSlide = ref(0)
const testimonialSlidesToShow = ref(3)
const isPrevHovered = ref(false)
const isNextHovered = ref(false)

const maxTestimonialSlide = computed(() => {
  if (!testimonials.value) return 0
  return Math.max(0, Math.ceil(testimonials.value.length / testimonialSlidesToShow.value) - 1)
})

// Função que retorna o estilo do botão (igual ao botão INSCREVA-SE)
const arrowButtonStyle = (isHovered) => {
  const baseColor = customization.value?.button_color || '#2563eb'
  const hoverColor = customization.value?.button_hover_color

  return {
    transition: 'background-color 0.3s ease',
    backgroundColor: isHovered && hoverColor ? hoverColor : baseColor
  }
}

const updateSlidesToShow = () => {
  if (typeof window === 'undefined') return
  testimonialSlidesToShow.value = window.innerWidth < 768 ? 1 : 3
}

const prevTestimonial = () => {
  if (testimonialSlide.value > 0) testimonialSlide.value--
}

const nextTestimonial = () => {
  if (testimonialSlide.value < maxTestimonialSlide.value) testimonialSlide.value++
}

const handleResize = () => {
  updateSlidesToShow()
  testimonialSlide.value = Math.min(testimonialSlide.value, maxTestimonialSlide.value)
}

onMounted(() => {
  updateSlidesToShow()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
button:disabled {
  cursor: not-allowed;
}
</style>